import { classifyCatalogImageUrl } from '../domain/googleLiveReferencePolicy';
import { matchVillageOwner } from '../domain/cazaRegisters';
import {
  distanceKm,
  isCoordinateInCatalogCaza,
  normalizeCatalogCaza,
  sharedCatalogCaza,
} from '../domain/tourismRules';
import { beirutDayKey, beirutDayStartUtcMs } from '../domain/eventTime';

export const ASK_ZGHARTA_STORAGE_KEY = 'zgharta-ask-plans:v2';
export const LEGACY_ASK_ZGHARTA_STORAGE_KEY = 'zgharta-ask-plans:v1';
export const SAVED_ASK_ZGHARTA_PLANS_VERSION = 2;
export const LEGACY_SAVED_ASK_ZGHARTA_PLANS_VERSION = 1;

export const EMPTY_SAVED_ASK_ZGHARTA_PLANS = {
  version: SAVED_ASK_ZGHARTA_PLANS_VERSION,
  plans: [],
};

const FOOD_CATEGORIES = new Set(['restaurant', 'cafe', 'shop']);
const VALID_STATUSES = new Set(['complete', 'partial', 'no_results', 'needs_clarification', 'error']);
const OUTSIDE_ZGHARTA_TERMS = ['batroun', 'beach day', 'البترون', 'البحر'];
const GOOGLE_MAPS_IMPORT_TERMS = ['google maps', 'google map', 'apple maps', 'خرائط جوجل', 'خرائط غوغل'];
const OPEN_NOW_TERMS = ['open now', 'open tonight', 'currently open', 'مفتوح الآن', 'مفتوحة الآن'];
const CURRENTNESS_TERMS = ['outdated', 'still worth', 'latest', 'new restaurants', 'tourist trap', 'قديم', 'جديد'];
const RESTAURANT_QUALITY_TERMS = [
  'best',
  'top',
  'recommended',
  'recommend',
  'authentic',
  'local favorite',
  'local favourite',
  'favorite',
  'favourite',
  'not touristy',
  'touristy',
  'where locals go',
  'good restaurant',
  'أفضل',
  'افضل',
  'أطيب',
  'اطيب',
  'محلي',
  'أصيل',
  'اصيل',
  'سياحي',
];
const BUSINESS_CATEGORY_TERMS = {
  restaurant: ['restaurant', 'restaurants', 'resturant', 'dinner', 'lunch', 'food', 'مطعم', 'مطاعم', 'غداء', 'عشاء', 'طعام'],
  cafe: ['cafe', 'cafes', 'coffee', 'coffe', 'cofee', 'مقهى', 'مقاهي', 'قهوة'],
  shop: ['shop', 'shops', 'shopping', 'gift', 'gifts', 'souvenir', 'souvenirs', 'sweets', 'dessert', 'bakery', 'local products', 'متجر', 'متاجر', 'تسوق', 'هدايا', 'حلويات', 'حلوى', 'مخبز', 'منتجات محلية'],
  hotel: ['hotel', 'hotels', 'guesthouse', 'guest house', 'stay', 'stays', 'lodging', 'accommodation', 'place to stay', 'فندق', 'فنادق', 'بيت ضيافة', 'إقامة'],
};
const CURRENT_LOCATION_TERMS = ['near me', 'nearby me', 'near my location', 'بالقرب مني', 'قربي'];
const ONLINE_FINDING_TERMS = [
  'online finding',
  'online findings',
  'new approved spot',
  'new approved spots',
  'approved new spot',
  'approved new spots',
  'approved spot',
  'approved spots',
  'approved row',
  'approved rows',
  'new spot',
  'new spots',
  'new place',
  'new places',
  'recently added',
  'fresh find',
  'fresh finds',
  'candidate spot',
  'candidate spots',
  'اكتشافات',
  'أماكن جديدة',
  'اماكن جديدة',
  'أماكن معتمدة',
  'اماكن معتمدة',
];
const BSHARRI_VILLAGE_TERMS = [
  'bsharri',
  'bcharre',
  'bcharri',
  'bcharreh',
  'bsharre',
  'becharre',
  'bane',
  'qadisha',
  'kadisha',
];
// Per-caza cross-attribution: a Zgharta-attributed row must not claim a Bsharri
// asset, and a Bsharri-attributed row must not claim a Zgharta asset.
const FORBIDDEN_CATALOG_TERMS = {
  zgharta: [
    'kadisha valley',
    'qadisha valley',
    'quadisha valley',
    'cedars of god',
    'arz el rab',
    'cedars hill',
    'qannoubine',
    'mar lichaa',
    'bsharri',
    'bcharre',
    'bane',
  ],
  bsharri: [
    'horsh ehden',
    'ehden midan',
    'qozhaya',
    'kibbeh zghartawi',
    'ehdeniyat',
  ],
};
const GLOBAL_FORBIDDEN_CATALOG_TERMS = [
  'hamatoura',
  'ehdenehden',
];
const ROAD_VILLAGE_TERMS = ['road', 'highway', 'autostrade', 'طريق', 'اتوستراد'];

const ACTION_LABELS = {
  open_detail: { en: 'Open details', ar: 'افتح التفاصيل' },
  view_on_map: { en: 'View on map', ar: 'اعرض على الخريطة' },
  save_plan: { en: 'Save plan', ar: 'احفظ الخطة' },
  share_plan: { en: 'Share plan', ar: 'شارك الخطة' },
  refine_request: { en: 'Refine request', ar: 'عدّل الطلب' },
};

export const ASK_ZGHARTA_GOLDEN_PROMPTS = [
  {
    id: 'en-outdated-restaurants',
    locale: 'en',
    prompt: 'Which restaurants are still worth it in Ehden, not outdated tourist traps?',
    coverage: ['restaurant_quality', 'currentness_limited'],
  },
  {
    id: 'en-best-local-restaurant',
    locale: 'en',
    prompt: 'What is the best authentic local restaurant in Ehden?',
    coverage: ['restaurant_quality', 'currentness_limited'],
  },
  {
    id: 'en-best-cafe',
    locale: 'en',
    prompt: 'What is the best cafe in Ehden?',
    coverage: ['cafe_quality', 'currentness_limited'],
  },
  {
    id: 'en-top-sweets',
    locale: 'en',
    prompt: 'Where is the top sweets or bakery stop?',
    coverage: ['shop_quality', 'currentness_limited'],
  },
  {
    id: 'en-best-hotel',
    locale: 'en',
    prompt: 'What is the best hotel or guesthouse near Ehden?',
    coverage: ['hotel_quality', 'currentness_limited'],
  },
  {
    id: 'en-short-ehden',
    locale: 'en',
    prompt: 'I have 2 hours in Ehden and want nature plus lunch.',
    coverage: ['short_visit', 'trail_food'],
  },
  {
    id: 'en-family-half-day',
    locale: 'en',
    prompt: 'Plan a relaxed half day with kids, easy walking, and dessert.',
    coverage: ['family', 'short_visit'],
  },
  {
    id: 'en-religious-coffee',
    locale: 'en',
    prompt: 'Give me churches, a local story, and coffee nearby.',
    coverage: ['religious_cultural'],
  },
  {
    id: 'en-shops',
    locale: 'en',
    prompt: 'Where can I shop for local products or sweets in Zgharta?',
    coverage: ['shops'],
  },
  {
    id: 'en-hotels',
    locale: 'en',
    prompt: 'Find a hotel or place to stay near Ehden.',
    coverage: ['hotels'],
  },
  {
    id: 'en-trail-lunch',
    locale: 'en',
    prompt: 'Easy hike with a good view and lunch after.',
    coverage: ['trail_food'],
  },
  {
    id: 'en-rainy-day',
    locale: 'en',
    prompt: 'It is raining. What can I do without a long outdoor walk?',
    coverage: ['rainy_day'],
  },
  {
    id: 'en-nearby',
    locale: 'en',
    prompt: 'What should I do near me in the next hour?',
    coverage: ['nearby', 'short_visit'],
  },
  {
    id: 'en-events-tonight',
    locale: 'en',
    prompt: 'What is happening today or tonight in Zgharta?',
    coverage: ['today_events'],
  },
  {
    id: 'en-open-now',
    locale: 'en',
    prompt: 'Find something open now with food after.',
    coverage: ['open_now'],
  },
  {
    id: 'en-google-place',
    locale: 'en',
    prompt: 'I found this place on Google Maps. Should I add it to my Zgharta plan?',
    coverage: ['google_maps_import'],
  },
  {
    id: 'en-outside-region',
    locale: 'en',
    prompt: 'Plan a beach day in Batroun from this app.',
    coverage: ['no_result'],
  },
  {
    id: 'ar-outdated-restaurants',
    locale: 'ar',
    prompt: 'ما هي المطاعم التي ما زالت جيدة في إهدن وليست قديمة؟',
    coverage: ['restaurant_quality', 'currentness_limited'],
  },
  {
    id: 'ar-best-local-restaurant',
    locale: 'ar',
    prompt: 'ما هو أفضل مطعم محلي وأصيل في إهدن؟',
    coverage: ['restaurant_quality', 'currentness_limited'],
  },
  {
    id: 'ar-best-cafe',
    locale: 'ar',
    prompt: 'ما هو أفضل مقهى في إهدن؟',
    coverage: ['cafe_quality', 'currentness_limited'],
  },
  {
    id: 'ar-top-sweets',
    locale: 'ar',
    prompt: 'أين أجد أطيب حلويات أو مخبز في زغرتا؟',
    coverage: ['shop_quality', 'currentness_limited'],
  },
  {
    id: 'ar-best-hotel',
    locale: 'ar',
    prompt: 'ما هو أفضل فندق أو بيت ضيافة قرب إهدن؟',
    coverage: ['hotel_quality', 'currentness_limited'],
  },
  {
    id: 'ar-short-ehden',
    locale: 'ar',
    prompt: 'لدي ساعتان في إهدن وأريد طبيعة وغداء.',
    coverage: ['short_visit', 'trail_food'],
  },
  {
    id: 'ar-family-half-day',
    locale: 'ar',
    prompt: 'خطط نصف يوم هادئ مع الأطفال ومشي سهل وحلوى.',
    coverage: ['family', 'short_visit'],
  },
  {
    id: 'ar-religious-coffee',
    locale: 'ar',
    prompt: 'أريد كنائس وقصة محلية وقهوة قريبة.',
    coverage: ['religious_cultural'],
  },
  {
    id: 'ar-shops',
    locale: 'ar',
    prompt: 'أين أشتري منتجات محلية أو حلويات في زغرتا؟',
    coverage: ['shops'],
  },
  {
    id: 'ar-hotels',
    locale: 'ar',
    prompt: 'اعثر على فندق أو مكان إقامة قرب إهدن.',
    coverage: ['hotels'],
  },
  {
    id: 'ar-trail-lunch',
    locale: 'ar',
    prompt: 'نزهة سهلة مع منظر جميل وغداء بعدها.',
    coverage: ['trail_food'],
  },
  {
    id: 'ar-rainy-day',
    locale: 'ar',
    prompt: 'الطقس ممطر. ماذا أفعل من دون مشي طويل في الخارج؟',
    coverage: ['rainy_day'],
  },
  {
    id: 'ar-nearby',
    locale: 'ar',
    prompt: 'ماذا أفعل بالقرب مني خلال الساعة القادمة؟',
    coverage: ['nearby', 'short_visit'],
  },
  {
    id: 'ar-events-tonight',
    locale: 'ar',
    prompt: 'ما هي الفعاليات اليوم أو الليلة في زغرتا؟',
    coverage: ['today_events'],
  },
  {
    id: 'ar-open-now',
    locale: 'ar',
    prompt: 'اعثر على مكان مفتوح الآن مع طعام بعده.',
    coverage: ['open_now'],
  },
  {
    id: 'ar-google-place',
    locale: 'ar',
    prompt: 'وجدت هذا المكان على خرائط غوغل. هل أضيفه إلى خطتي في زغرتا؟',
    coverage: ['google_maps_import'],
  },
  {
    id: 'ar-outside-region',
    locale: 'ar',
    prompt: 'خطط لي يوما على البحر في البترون من هذا التطبيق.',
    coverage: ['no_result'],
  },
];

export const ASK_ZGHARTA_EXAMPLES = {
  en: [
    'I have 2 hours in Ehden and want nature plus lunch.',
    'Easy hike with a good view and lunch after.',
    'Give me churches, a local story, and coffee nearby.',
    'Find a hotel or place to stay near Ehden.',
    'What is happening today or tonight in Zgharta?',
  ],
  ar: [
    'لدي ساعتان في إهدن وأريد طبيعة وغداء.',
    'نزهة سهلة مع منظر جميل وغداء بعدها.',
    'أريد كنائس وقصة محلية وقهوة قريبة.',
    'اعثر على فندق أو مكان إقامة قرب إهدن.',
    'ما هي الفعاليات اليوم أو الليلة في زغرتا؟',
  ],
};

const normalize = value => String(value || '')
  .normalize('NFKC')
  .replace(/[\u064B-\u065F\u0670]/g, '')
  .trim()
  .toLocaleLowerCase();

const includesAny = (query, terms) => {
  const lower = normalize(query);
  return terms.some(term => lower.includes(term.toLocaleLowerCase()));
};

const normalizeForNameSearch = value => String(value || '')
  .normalize('NFKC')
  .toLocaleLowerCase()
  .replace(/[\u064B-\u065F\u0670]/g, '')
  .replace(/[أإآٱ]/g, 'ا')
  .replace(/ى/g, 'ي')
  .replace(/ة/g, 'ه')
  .replace(/[^\p{L}\p{N}]+/gu, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const NAME_MATCH_SCORE = {
  exact: 620,
  contained: 560,
  partial: 430,
};

const catalogNameMatchScore = (item, request) => {
  const query = normalizeForNameSearch(request?.query);
  if (!query) return 0;

  const names = [item?.name, item?.nameAr]
    .map(normalizeForNameSearch)
    .filter(Boolean);

  return names.reduce((best, name) => {
    if (query === name) return Math.max(best, NAME_MATCH_SCORE.exact);
    if (query.includes(name)) return Math.max(best, NAME_MATCH_SCORE.contained);
    if (name.length >= 6 && name.includes(query)) return Math.max(best, NAME_MATCH_SCORE.partial);
    return best;
  }, 0);
};

const attributedCaza = item => {
  const explicit = normalizeCatalogCaza(item?.caza);
  if (explicit) return explicit;
  const village = normalize(item?.village);
  if (village && BSHARRI_VILLAGE_TERMS.some(term => village.includes(term))) return 'bsharri';
  if (village === 'zgharta') return 'zgharta';
  const lat = item?.coordinates?.lat;
  const lng = item?.coordinates?.lng;
  if (Number.isFinite(lat) && Number.isFinite(lng)) {
    const inZgharta = isCoordinateInCatalogCaza(lat, lng, 'zgharta');
    const inBsharri = isCoordinateInCatalogCaza(lat, lng, 'bsharri');
    if (inZgharta !== inBsharri) return inZgharta ? 'zgharta' : 'bsharri';
  }
  // Legacy event fixtures and cached rows predate the caza column. An exact
  // supported-village match is enough to recover those event rows, but never
  // becomes a general fallback for places or businesses.
  if (item?.date) return matchVillageOwner(item.village)?.caza || null;
  return null;
};

export const isAskZghartaCatalogItemAllowed = item => {
  if (item?.category === 'health') return false;
  const searchable = [
    item?.name,
    item?.nameAr,
    item?.village,
    item?.description,
    item?.descriptionAr,
  ].map(normalize).join(' ');
  const village = normalize(item?.village);

  if (GLOBAL_FORBIDDEN_CATALOG_TERMS.some(term => searchable.includes(term))) return false;
  if (village && ROAD_VILLAGE_TERMS.some(term => village.includes(term))) return false;
  const caza = attributedCaza(item);
  if (!caza) return false;
  const crossTerms = FORBIDDEN_CATALOG_TERMS[caza] || [];
  if (crossTerms.some(term => searchable.includes(term))) return false;
  return true;
};

const firstBusinessByCategoryPreference = (businesses, categories) => {
  for (const category of categories) {
    const match = businesses.find(business => business.category === category);
    if (match) return match;
  }
  return null;
};

export const localizedAskZghartaText = (text, locale) => {
  if (!text) return '';
  return locale === 'ar' ? (text.ar || '') : (text.en || '');
};

const localizedName = item => {
  const caza = normalizeCatalogCaza(item?.caza);
  const fallback = caza === 'bsharri'
    ? { en: 'Bsharri stop', ar: 'محطة في بشري' }
    : caza === 'zgharta'
      ? { en: 'Zgharta stop', ar: 'محطة في زغرتا' }
      : { en: 'North Lebanon stop', ar: 'محطة في شمال لبنان' };
  return {
    en: item.name || fallback.en,
    ar: item.nameAr || fallback.ar,
  };
};

const action = type => ({
  type,
  label: ACTION_LABELS[type],
});

const hasCoordinates = item => (
  typeof item?.coordinates?.lat === 'number' &&
  typeof item?.coordinates?.lng === 'number' &&
  Number.isFinite(item.coordinates.lat) &&
  Number.isFinite(item.coordinates.lng)
);

const hasUsefulText = value => String(value || '').trim().length >= 24;

// Active-language script guards — mirror src/App.js's ARABIC_SCRIPT_RE / LATIN_SCRIPT_RE /
// textMatchesActiveLanguage exactly so planner copy falls back the same way the detail modal does.
// EN copy must be Arabic-free; AR copy must be Latin-free. Prevents a mismatched-script
// description (e.g. Latin text stored in descriptionAr) from leaking into the wrong language.
const ARABIC_SCRIPT_RE = /[؀-ۿ]/;
const LATIN_SCRIPT_RE = /[A-Za-z]/;
const looksEnglish = value => {
  const text = String(value || '').trim();
  return Boolean(text) && !ARABIC_SCRIPT_RE.test(text);
};
const looksArabic = value => {
  const text = String(value || '').trim();
  return Boolean(text) && !LATIN_SCRIPT_RE.test(text);
};

const hasCatalogDescription = item => {
  if (item?.publicDescriptionPendingFirstPartyConfirmation) return false;
  return hasUsefulText(item?.description) || hasUsefulText(item?.descriptionAr);
};

const hasCatalogImage = item => {
  if (!/^https?:\/\//i.test(String(item?.image || '').trim())) return false;
  return !classifyCatalogImageUrl(item.image).needsFirstPartyReplacement;
};

const hasLocalizedName = item => Boolean(String(item?.name || '').trim() && String(item?.nameAr || '').trim());

const APPROVED_COPY_LABELS = new Set([
  'approved first-party copy',
  'first-party approved',
]);

const toEditorialLabelList = labels => {
  if (Array.isArray(labels)) return labels;
  if (labels == null) return [];
  return [labels];
};

const hasApprovedCopySignal = item => toEditorialLabelList(item?.editorialLabels)
  .some(label => APPROVED_COPY_LABELS.has(String(label).trim().toLowerCase()));

const hasOnlineFindingIntent = request => includesAny(request.query, ONLINE_FINDING_TERMS);

const ONLINE_FINDING_LABEL = 'online finding';

const isApprovedOnlineFindingCandidate = item => {
  const id = String(item?.id || '');
  const hasOnlineFindingMarker = Boolean(
    id.startsWith('pending-') ||
    id.startsWith('online-finding-') ||
    toEditorialLabelList(item?.editorialLabels)
      .some(label => String(label).trim().toLowerCase() === ONLINE_FINDING_LABEL),
  );

  return hasOnlineFindingMarker && hasApprovedCopySignal(item);
};

const demoReadinessScore = item => {
  let score = 0;
  if (hasApprovedCopySignal(item)) score += 90;
  if (hasCatalogImage(item)) score += 28;
  if (hasCatalogDescription(item)) score += 24;
  if (hasLocalizedName(item)) score += 10;
  if (Array.isArray(item?.editorialLabels) && item.editorialLabels.length > 0) score += 8;
  if (item?.catalogLocalityNeedsReview || item?.catalogLocalityFilterable === false) score -= 110;
  return score;
};

// Re-exported (single canonical impl now lives in ../domain/tourismRules) so
// server tests can assert the CJS mirror in api/lib/askZgharta/routeSanity.js
// stays equivalent (#208, #254).
export { distanceKm };

const inferVillage = (catalog, request) => {
  if (request.context?.village) return request.context.village;

  const query = normalize(request.query);
  const villages = new Set([
    ...(catalog.places || []),
    ...(catalog.businesses || []),
    ...(catalog.events || []),
  ].map(item => item.village).filter(Boolean));

  for (const village of villages) {
    if (query.includes(village.toLocaleLowerCase())) return village;
  }

  return null;
};

const TOP_TIER_SIZE = 4;
const TOP_TIER_SCORE_WINDOW = 2;

const createSeededRandom = seed => {
  let state = [...String(seed)].reduce(
    (hash, char) => Math.imul(hash ^ char.charCodeAt(0), 16777619) >>> 0,
    2166136261,
  );
  return () => {
    state += 0x6D2B79F5;
    let value = state;
    value = Math.imul(value ^ (value >>> 15), value | 1);
    value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
    return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
  };
};

const seededShuffle = (items, seed) => {
  const shuffled = [...items];
  const random = createSeededRandom(seed);
  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(random() * (index + 1));
    [shuffled[index], shuffled[swapIndex]] = [shuffled[swapIndex], shuffled[index]];
  }
  return shuffled;
};

const weightedTopTierOrder = (ranked, seed, groupFor) => {
  const result = [...ranked];
  const groups = {};
  ranked.forEach((entry, index) => {
    const group = groupFor(entry.item);
    (groups[group] ||= []).push({ entry, index });
  });

  Object.entries(groups).forEach(([group, candidates]) => {
    const bestScore = candidates[0]?.entry.score;
    const topTier = candidates
      .slice(0, TOP_TIER_SIZE)
      .filter(candidate => candidate.entry.score >= bestScore - TOP_TIER_SCORE_WINDOW);
    if (topTier.length < 2) return;

    const floorScore = topTier[topTier.length - 1].entry.score;
    const random = createSeededRandom(`${seed}:${group}`);
    const shuffled = topTier
      .map(candidate => ({
        candidate,
        key: Math.pow(
          Math.max(random(), Number.EPSILON),
          1 / (1 + (candidate.entry.score - floorScore) / 10),
        ),
      }))
      .sort((a, b) => b.key - a.key)
      .map(weighted => weighted.candidate.entry);

    topTier.forEach((candidate, index) => {
      result[candidate.index] = shuffled[index];
    });
  });

  return result;
};

const rankByVillageAndDistance = (items, request, village, baseScore, categoryScores = {}, seed = '') => (
  weightedTopTierOrder(
    [...(items || [])]
      .map(item => {
        let score = baseScore(item) + demoReadinessScore(item) + catalogNameMatchScore(item, request);
        if (categoryScores[item.category]) score += categoryScores[item.category];
        if (village && item.village === village) score += 120;
        if (hasCoordinates(item)) score += 5;
        if (request.context?.currentCoordinate && hasCoordinates(item)) {
          score += Math.max(0, 220 - distanceKm(request.context.currentCoordinate, item.coordinates) * 35);
        }
        return { item, score };
      })
      .sort((a, b) => b.score - a.score || String(a.item.name || '').localeCompare(String(b.item.name || ''))),
    seed,
    item => item.category || 'uncategorized',
  )
    .map(ranked => ranked.item)
);

const rankEvents = (events, request, now, village, seed) => {
  // Anchor "today" to the Beirut calendar day so the upcoming/today boundary
  // matches every other event surface (see src/domain/eventTime.js). Event
  // dates are date-only strings, so their own key is stable.
  const todayKey = beirutDayKey(now);
  const todayStartMs = beirutDayStartUtcMs(now);
  const visitDateKey = request.context?.visitDate ? beirutDayKey(request.context.visitDate) : null;
  const wantsTonight = includesAny(request.query, ['tonight', 'today', 'this evening', 'الليلة', 'اليوم', 'هذا المساء']);

  const ranked = [...(events || [])]
    .filter(event => {
      const eventKey = beirutDayKey(event.date);
      if (eventKey == null) return false;
      if (wantsTonight && visitDateKey) return eventKey === visitDateKey;
      return todayKey == null || eventKey >= todayKey;
    })
    .map(event => {
      const eventStartMs = beirutDayStartUtcMs(event.date);
      const daysAway = (eventStartMs != null && todayStartMs != null)
        ? Math.max(0, Math.round((eventStartMs - todayStartMs) / 86400000))
        : 0;
      return {
        item: event,
        score: (event.featured ? 30 : 0) +
          (village && event.village === village ? 70 : 0) +
          (todayKey != null && beirutDayKey(event.date) === todayKey ? 120 : 0) +
          Math.max(0, 80 - daysAway * 4),
      };
    })
    .sort((a, b) => b.score - a.score || String(a.item.name || '').localeCompare(String(b.item.name || '')));

  return weightedTopTierOrder(ranked, seed, () => 'events').map(entry => entry.item);
};

const allowedItems = items => (items || []).filter(isAskZghartaCatalogItemAllowed);

const hasIntent = (request, intent) => {
  switch (intent) {
    case 'nature':
      return includesAny(request.query, ['nature', 'view', 'hike', 'trail', 'outdoor', 'طبيعة', 'منظر', 'نزهة', 'مشي']);
    case 'food':
      return includesAny(request.query, ['food', 'lunch', 'dinner', 'coffee', 'coffe', 'cofee', 'cafe', 'cafes', 'dessert', 'restaurant', 'restaurants', 'resturant', 'غداء', 'عشاء', 'قهوة', 'مقهى', 'مقاهي', 'حلوى', 'طعام', 'مطعم', 'مطاعم']);
    case 'religious':
      return includesAny(request.query, ['church', 'churches', 'religious', 'cultural', 'story', 'كنائس', 'كنيسة', 'ديني', 'قصة']);
    case 'family':
      return includesAny(request.query, ['kids', 'family', 'relaxed', 'children', 'أطفال', 'العائلة', 'هادئ']);
    case 'rainy':
      return includesAny(request.query, ['rain', 'raining', 'rainy', 'indoor', 'without a long outdoor walk', 'ممطر', 'المطر', 'داخل']);
    case 'nearby':
      return includesAny(request.query, ['near me', 'nearby', 'قربي', 'بالقرب مني']);
    case 'event':
      return includesAny(request.query, ['event', 'happening', 'tonight', 'today', 'فعاليات', 'اليوم', 'الليلة']);
    case 'shop':
      return includesAny(request.query, ['shop', 'shopping', 'sweets', 'souvenir', 'local products', 'bakery', 'أشتري', 'تسوق', 'حلويات', 'منتجات محلية']);
    case 'hotel':
      return includesAny(request.query, ['hotel', 'stay', 'sleep', 'accommodation', 'place to stay', 'فندق', 'إقامة', 'أنام']);
    default:
      return false;
  }
};

const categoryScoresForRequest = request => {
  const scores = {};
  const add = (category, points) => { scores[category] = (scores[category] || 0) + points; };
  const preferredCategory = preferredBusinessCategory(request);

  if (hasIntent(request, 'nature')) add('nature', 90);
  if (hasIntent(request, 'religious')) {
    add('religious', 100);
    add('heritage', 45);
  }
  if (hasIntent(request, 'family')) {
    add('heritage', 45);
    add('nature', 25);
    add('shop', 20);
  }
  if (hasIntent(request, 'rainy')) {
    add('heritage', 85);
    add('religious', 65);
    add('cafe', 45);
    add('nature', -70);
  }
  if (hasIntent(request, 'shop')) add('shop', 100);
  if (hasIntent(request, 'hotel')) add('hotel', 110);
  if (preferredCategory) add(preferredCategory, 120);
  if (hasIntent(request, 'food')) {
    add('restaurant', 65);
    add('cafe', 45);
    add('shop', 25);
  }

  return scores;
};

const warning = (code, en, ar) => ({
  code,
  message: { en, ar },
});

const preferredBusinessCategory = request => {
  for (const category of ['hotel', 'cafe', 'shop', 'restaurant']) {
    if (includesAny(request.query, BUSINESS_CATEGORY_TERMS[category])) return category;
  }

  return null;
};

const asksBusinessQualityFreshness = request => (
  includesAny(request.query, CURRENTNESS_TERMS) ||
  (preferredBusinessCategory(request) && includesAny(request.query, RESTAURANT_QUALITY_TERMS))
);

const unsupportedWarnings = request => {
  const warnings = [];

  if (includesAny(request.query, OPEN_NOW_TERMS)) {
    warnings.push(warning(
      'open_now_unsupported',
      'Open-now checks are not connected to live hours yet. Open each listing, call when a phone number is available, and confirm hours before you go.',
      'فحص المفتوح الآن غير متصل بساعات مباشرة بعد. افتح كل بطاقة واتصل عند توفر رقم هاتف وتأكد من ساعات العمل قبل الذهاب.',
    ));
  }

  if (asksBusinessQualityFreshness(request)) {
    warnings.push(warning(
      'currentness_limited',
      'The planner ranks known directory businesses by rating, verification, village, and coordinates, but cannot prove the data is fresh. Open each listing, call when a phone number is available, and confirm hours, operator status, and photos before relying on it.',
      'يرتب المخطط الأعمال المعروفة في الدليل حسب التقييم والتوثيق والقرية والإحداثيات، لكنه لا يثبت أن البيانات حديثة. افتح كل بطاقة واتصل عند توفر رقم هاتف وتأكد من ساعات العمل وحالة المشغل والصور المعتمدة قبل الاعتماد عليها.',
    ));
  }

  return warnings;
};

// Per-category fallback hooks — honest, caza-safe, no fabricated specifics. Used only when no
// approved first-party description is available for the relevant language.
const HOOK_FALLBACK = {
  restaurant: { en: v => `A local table to settle into${v ? ` in ${v}` : ''}.`, ar: v => `مطعم محلي${v ? ` في ${v}` : ''}.` },
  cafe: { en: v => `A spot to slow down over coffee${v ? ` in ${v}` : ''}.`, ar: v => `مكان للقهوة والاستراحة${v ? ` في ${v}` : ''}.` },
  shop: { en: v => `A local shop worth a browse${v ? ` in ${v}` : ''}.`, ar: v => `متجر محلي يستحق التصفّح${v ? ` في ${v}` : ''}.` },
  hotel: { en: v => `A place to rest up${v ? ` in ${v}` : ''}.`, ar: v => `مكان للإقامة والراحة${v ? ` في ${v}` : ''}.` },
  heritage: { en: v => `A piece of local heritage${v ? ` in ${v}` : ''}.`, ar: v => `معلم تراثي${v ? ` في ${v}` : ''}.` },
  nature: { en: v => `A breath of the outdoors${v ? ` in ${v}` : ''}.`, ar: v => `متنفّس في الطبيعة${v ? ` في ${v}` : ''}.` },
  religious: { en: v => `A quiet place of worship${v ? ` in ${v}` : ''}.`, ar: v => `مكان للصلاة والسكينة${v ? ` في ${v}` : ''}.` },
  _default: { en: v => `A stop worth making${v ? ` in ${v}` : ''}.`, ar: v => `محطة تستحق التوقف${v ? ` في ${v}` : ''}.` },
};

const trimHook = (text, max = 160) => {
  const clean = String(text || '').replace(/\s+/g, ' ').trim();
  if (clean.length <= max) return clean;
  const cut = clean.slice(0, max);
  const lastSpace = cut.lastIndexOf(' ');
  return `${(lastSpace > 40 ? cut.slice(0, lastSpace) : cut).replace(/[\s.,;:!?،]+$/, '')}…`;
};

const categoryFallbackHook = (item, vEn, vAr) => {
  const tmpl = HOOK_FALLBACK[item?.category] || HOOK_FALLBACK._default;
  return { en: tmpl.en(vEn), ar: tmpl.ar(vAr) };
};

// Place-describing recommendation copy: sell what the place IS, not why the planner matched it.
// Uses the approved first-party description per language when present (gated by the
// pending-confirmation flag), else an honest category + village fallback. Never fabricates.
const recommendationHook = (item, type, village) => {
  const vEn = item?.village || village || '';
  const vAr = item?.villageAr || item?.village || village || '';
  const pending = Boolean(item?.publicDescriptionPendingFirstPartyConfirmation);
  const fallback = type === 'event'
    ? { en: `An upcoming event${vEn ? ` in ${vEn}` : ''}.`, ar: `فعالية قادمة${vAr ? ` في ${vAr}` : ''}.` }
    : categoryFallbackHook(item, vEn, vAr);
  return {
    en: (!pending && hasUsefulText(item?.description) && looksEnglish(item.description)) ? trimHook(item.description) : fallback.en,
    ar: (!pending && hasUsefulText(item?.descriptionAr) && looksArabic(item.descriptionAr)) ? trimHook(item.descriptionAr) : fallback.ar,
  };
};

// Structured, already-vetted display facts for the card (rating/specialties are business-only).
// Built from the same gated catalog item the planner ranked, so the card shows only vetted content.
const catalogMeta = (item, type) => ({
  category: item?.category || null,
  village: item?.village || null,
  villageAr: item?.villageAr || null,
  caza: normalizeCatalogCaza(item?.caza) || null,
  rating: (type === 'business' && typeof item?.rating === 'number') ? item.rating : null,
  verified: type === 'business' ? Boolean(item?.verified) : false,
  date: type === 'event' ? (item?.date || null) : null,
  time: type === 'event' ? (item?.time || null) : null,
  specialties: (type === 'business' && Array.isArray(item?.specialties))
    ? item.specialties.map(s => String(s || '').trim()).filter(Boolean).slice(0, 3)
    : [],
});

const reasonForCatalogItem = (item, type, request, village, now) => recommendationHook(item, type, village);

const stableHash = value => {
  const text = String(value);
  const legacy = Math.abs(
    [...text].reduce((hash, char) => (hash * 31 + char.charCodeAt(0)) | 0, 7),
  );
  const fnv = [...text].reduce(
    (hash, char) => Math.imul(hash ^ char.charCodeAt(0), 16777619) >>> 0,
    2166136261,
  );
  return `${legacy}_${fnv}`;
};

const planId = request => `ask_zgharta_${stableHash(`${request.createdAt}:${request.locale}:${request.query}`)}`;

const catalogItem = (ref, item, reason, actions = [action('open_detail'), action('view_on_map')]) => ({
  id: `${ref.type}-${ref.id}`,
  source: 'catalog',
  ref,
  title: localizedName(item),
  reason,
  meta: catalogMeta(item, ref.type),
  actions,
});

export function buildAskZghartaReferenceIndex(catalog = {}) {
  return {
    places: new Set(allowedItems(catalog.places).map(place => String(place.id))),
    businesses: new Set(allowedItems(catalog.businesses).map(business => String(business.id))),
    events: new Set(allowedItems(catalog.events).map(event => String(event.id))),
    trails: new Set(),
  };
}

export function askZghartaOwnershipCopy(itemsOrStops, locale = 'en') {
  const caza = sharedCatalogCaza(itemsOrStops);
  if (caza === 'bsharri') {
    return locale === 'ar'
      ? { title: 'خطتك في بشري', summary: 'تم بناؤها من أماكن وأعمال وفعاليات معروفة في قضاء بشري.', share: 'خطة قضاء بشري' }
      : { title: 'Your Bsharri plan', summary: 'Built from known Bsharri places, businesses, and events.', share: 'Bsharri Caza plan' };
  }
  if (caza === 'zgharta') {
    return locale === 'ar'
      ? { title: 'خطتك في زغرتا', summary: 'تم بناؤها من أماكن وأعمال وفعاليات معروفة في زغرتا.', share: 'خطة قضاء زغرتا' }
      : { title: 'Your Zgharta plan', summary: 'Built from known Zgharta places, businesses, and events.', share: 'Zgharta Caza plan' };
  }
  return locale === 'ar'
    ? { title: 'خطتك في شمال لبنان', summary: 'تم بناؤها من أماكن وأعمال وفعاليات معروفة في شمال لبنان.', share: 'خطة شمال لبنان' }
    : { title: 'Your North Lebanon plan', summary: 'Built from known North Lebanon places, businesses, and events.', share: 'North Lebanon plan' };
}

const noResultPlan = (request, summary, warnings = []) => ({
  schemaVersion: 1,
  id: planId(request),
  status: 'no_results',
  locale: request.locale,
  request,
  title: {
    en: 'No matching Zgharta plan',
    ar: 'لا توجد خطة مطابقة في زغرتا',
  },
  summary: summary || {
    en: 'This app is focused on Zgharta Caza, and the current directory does not support that request yet.',
    ar: 'يركز هذا التطبيق على قضاء زغرتا، ولا يحتوي الدليل الحالي على ما يكفي لهذا الطلب.',
  },
  sections: [],
  warnings,
  actions: [action('refine_request')],
});

const planTitleForStops = (items, status) => {
  const caza = sharedCatalogCaza(items);
  if (status === 'partial') {
    if (caza === 'bsharri') return { en: 'A practical Bsharri starting plan', ar: 'خطة بداية عملية في بشري' };
    if (caza === 'zgharta') return { en: 'A practical Zgharta starting plan', ar: 'خطة بداية عملية في زغرتا' };
    return { en: 'A practical North Lebanon starting plan', ar: 'خطة بداية عملية في شمال لبنان' };
  }
  return {
    en: askZghartaOwnershipCopy(items, 'en').title,
    ar: askZghartaOwnershipCopy(items, 'ar').title,
  };
};

const buildPlan = (request, items, warnings = [], village = null, now = new Date()) => {
  if (items.length === 0) return noResultPlan(request);
  const status = warnings.length > 0 ? 'partial' : 'complete';
  const stopItems = items.map(({ item }) => item);

  return {
    schemaVersion: 1,
    id: planId(request),
    status,
    locale: request.locale,
    request,
    title: planTitleForStops(stopItems, status),
    summary: {
      en: askZghartaOwnershipCopy(stopItems, 'en').summary,
      ar: askZghartaOwnershipCopy(stopItems, 'ar').summary,
    },
    sections: [{
      id: 'recommended-stops',
      title: {
        en: 'Recommended stops',
        ar: 'محطات مقترحة',
      },
      items: items.map(({ item, type }) => catalogItem(
        { type, id: item.id },
        item,
        reasonForCatalogItem(item, type, request, village, now),
        type === 'event' ? [action('open_detail')] : [action('open_detail'), action('view_on_map')],
      )),
    }],
    warnings,
    actions: [action('save_plan'), action('share_plan')],
  };
};

export function planAskZghartaFromCatalog({ catalog = {}, request, now = new Date() }) {
  if (!request?.query?.trim()) return noResultPlan({ ...request, query: '', locale: request?.locale || 'en', createdAt: new Date(now).toISOString() });
  if (includesAny(request.query, OUTSIDE_ZGHARTA_TERMS)) return noResultPlan(request);
  if (includesAny(request.query, GOOGLE_MAPS_IMPORT_TERMS)) {
    return noResultPlan(request, {
      en: 'Google Maps import is not connected on the web app yet. Search the Zgharta directory here, then save matching stops.',
      ar: 'استيراد خرائط غوغل غير متصل في تطبيق الويب بعد. ابحث في دليل زغرتا هنا ثم احفظ المحطات المطابقة.',
    }, [warning(
      'google_maps_import_unsupported',
      'The release path cannot add or recommend unlisted Google Maps places.',
      'لا يمكن لمسار الإصدار إضافة أو اقتراح أماكن غير موجودة في الدليل من خرائط غوغل.',
    )]);
  }
  if (includesAny(request.query, CURRENT_LOCATION_TERMS) && !request.context?.currentCoordinate) {
    return noResultPlan(request, {
      en: 'Nearby planning needs a one-time current location. The web app does not have one for this request.',
      ar: 'يتطلب التخطيط القريب موقعا حاليا لمرة واحدة، ولا يملكه تطبيق الويب لهذا الطلب.',
    }, [warning(
      'missing_current_location',
      'Share a current location to rank nearby stops by distance.',
      'شارك موقعا حاليا لترتيب المحطات القريبة حسب المسافة.',
    )]);
  }

  const village = inferVillage(catalog, request);
  const requestSeed = `${request.createdAt}:${request.locale}:${request.query}`;
  const categoryScores = categoryScoresForRequest(request);
  const rankedPlaces = rankByVillageAndDistance(
    allowedItems(catalog.places),
    request,
    village,
    place => (place.featured ? 30 : 0),
    categoryScores,
    `${requestSeed}:places`,
  );
  const rankedBusinesses = rankByVillageAndDistance(
    allowedItems(catalog.businesses),
    request,
    village,
    business => (business.verified ? 30 : 0) + (business.rating || 0) * 12,
    categoryScores,
    `${requestSeed}:businesses`,
  );
  const rankedEvents = rankEvents(allowedItems(catalog.events), request, now, village, `${requestSeed}:events`);
  const items = [];
  let requestedFoodCategories = [];
  const warnings = unsupportedWarnings(request);
  const namedItems = [
    ...rankedPlaces
      .filter(place => catalogNameMatchScore(place, request) > 0)
      .map(place => ({ item: place, type: 'place' })),
    ...rankedBusinesses
      .filter(business => catalogNameMatchScore(business, request) > 0)
      .map(business => ({ item: business, type: 'business' })),
    ...rankedEvents
      .filter(event => catalogNameMatchScore(event, request) > 0)
      .map(event => ({ item: event, type: 'event' })),
  ].sort((a, b) => (
    catalogNameMatchScore(b.item, request) - catalogNameMatchScore(a.item, request) ||
    String(a.item.name || '').localeCompare(String(b.item.name || ''))
  ));

  items.push(...namedItems);

  const hasOnlineFindings = hasOnlineFindingIntent(request);
  if (hasOnlineFindings) {
    items.push(
      ...rankedPlaces
        .filter(isApprovedOnlineFindingCandidate)
        .map(place => ({ item: place, type: 'place' })),
      ...rankedBusinesses
        .filter(isApprovedOnlineFindingCandidate)
        .map(business => ({ item: business, type: 'business' })),
    );
  }

  if (hasIntent(request, 'event')) {
    items.push(...rankedEvents.slice(0, 2).map(event => ({ item: event, type: 'event' })));
  }

  if (hasIntent(request, 'religious')) {
    const wantsChurch = includesAny(request.query, ['church', 'churches', 'كنائس', 'كنيسة']);
    const religious = (wantsChurch ? rankedPlaces.find(place => place.category === 'religious') : null) ||
      rankedPlaces.find(place => place.category === 'religious' || place.category === 'heritage');
    if (religious) items.push({ item: religious, type: 'place' });
  }

  if (hasIntent(request, 'rainy')) {
    const indoorPlace = rankedPlaces.find(candidate => candidate.category === 'heritage' || candidate.category === 'religious');
    if (indoorPlace) items.push({ item: indoorPlace, type: 'place' });
  }

  if (hasIntent(request, 'nature') || hasIntent(request, 'family') || hasIntent(request, 'nearby')) {
    const place = hasIntent(request, 'nearby')
      ? rankedPlaces[0]
      : hasIntent(request, 'family')
        ? rankedPlaces.find(candidate => candidate.category === 'heritage') ||
          rankedPlaces.find(candidate => candidate.category === 'nature')
      : hasIntent(request, 'rainy')
        ? null
        : rankedPlaces.find(candidate => candidate.category === 'nature') ||
        rankedPlaces.find(candidate => candidate.category === 'heritage');
    if (place) items.push({ item: place, type: 'place' });
  }

  if (hasIntent(request, 'hotel')) {
    const hotel = rankedBusinesses.find(business => business.category === 'hotel');
    if (hotel) items.push({ item: hotel, type: 'business' });
  }

  if (hasIntent(request, 'shop')) {
    const shop = rankedBusinesses.find(business => business.category === 'shop');
    if (shop) items.push({ item: shop, type: 'business' });
  }

  if (hasIntent(request, 'food') || hasIntent(request, 'family') || hasIntent(request, 'religious') || hasIntent(request, 'rainy')) {
    const wantsCoffee = includesAny(request.query, ['coffee', 'coffe', 'cofee', 'cafe', 'cafes', 'قهوة', 'مقهى', 'مقاهي']);
    const wantsDessert = includesAny(request.query, ['dessert', 'sweets', 'حلوى', 'حلويات']);
    const wantsMeal = includesAny(request.query, ['food', 'lunch', 'dinner', 'meal', 'restaurant', 'restaurants', 'resturant', 'غداء', 'عشاء', 'طعام', 'مطعم', 'مطاعم']);
    // Map each explicitly named food sub-type to its own catalog category (canonical order).
    requestedFoodCategories = [
      wantsMeal && 'restaurant',
      wantsCoffee && 'cafe',
      wantsDessert && 'shop',
    ].filter(Boolean);

    if (requestedFoodCategories.length >= 2) {
      // Distinct desires get distinct stops: coffee -> cafe, meal -> restaurant, dessert -> shop.
      // Exact category only (no cross-category fallback) so a "coffee" stop is never a mislabeled restaurant.
      requestedFoodCategories.forEach(category => {
        const pick = firstBusinessByCategoryPreference(rankedBusinesses, [category]);
        if (pick) items.push({ item: pick, type: 'business' });
      });
    } else {
      // Single (or no) named sub-type: pick one food stop, ordered by what was asked.
      // Coffee — and rainy days without a meal — lead with cafes; dessert leads with shops.
      const cafeFirst = wantsCoffee || (hasIntent(request, 'rainy') && !wantsMeal);
      const categoryPreference = cafeFirst
        ? ['cafe', 'restaurant', 'shop']
        : wantsDessert
          ? ['shop', 'cafe', 'restaurant']
          : ['restaurant', 'cafe', 'shop'];
      const food = firstBusinessByCategoryPreference(rankedBusinesses, categoryPreference) ||
        rankedBusinesses.find(business => FOOD_CATEGORIES.has(business.category));
      if (food) items.push({ item: food, type: 'business' });
    }
  }

  const hasAnySpecificIntent = hasOnlineFindings || ['event', 'religious', 'nature', 'family', 'nearby', 'food', 'rainy', 'shop', 'hotel'].some(intent => hasIntent(request, intent));
  if (!hasAnySpecificIntent) {
    if (rankedPlaces[0]) items.push({ item: rankedPlaces[0], type: 'place' });
    if (rankedBusinesses[0]) items.push({ item: rankedBusinesses[0], type: 'business' });
  }

  if (hasIntent(request, 'nature') && includesAny(request.query, ['hike', 'trail', 'نزهة', 'مشي'])) {
    warnings.push(warning(
      'trail_data_limited',
      'Trail tracking is not connected to the web directory yet, so this plan uses nature stops.',
      'تتبع المسارات غير متصل بدليل الويب بعد، لذلك تستخدم هذه الخطة محطات طبيعية.',
    ));
  }

  const seen = new Set();
  const deduped = items.filter(({ item, type }) => {
    const key = `${type}:${item.id}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  const selected = deduped.slice(0, 4);
  const namedKeys = new Set(namedItems.map(({ item, type }) => `${type}:${item.id}`));
  const ordered = requestedFoodCategories.length >= 2
    ? [
      ...selected.filter(({ item, type }) => namedKeys.has(`${type}:${item.id}`)),
      ...seededShuffle(
        selected.filter(({ item, type }) => !namedKeys.has(`${type}:${item.id}`)),
        `${requestSeed}:stop-order`,
      ),
    ]
    : selected;

  return buildPlan(request, ordered, warnings, village, now);
}

const isLocalizedText = value => (
  value &&
  typeof value === 'object' &&
  typeof value.en === 'string' &&
  value.en.trim().length > 0 &&
  typeof value.ar === 'string' &&
  value.ar.trim().length > 0
);

const REF_COLLECTIONS = {
  place: 'places',
  business: 'businesses',
  event: 'events',
  trail: 'trails',
};

export function validateAskZghartaPlan(plan, references = buildAskZghartaReferenceIndex()) {
  const errors = [];
  if (!plan || typeof plan !== 'object') return { ok: false, errors: ['Plan must be an object.'] };
  if (plan.schemaVersion !== 1) errors.push('Plan schemaVersion must be 1.');
  if (!VALID_STATUSES.has(plan.status)) errors.push('Plan status is invalid.');
  if (plan.locale !== 'en' && plan.locale !== 'ar') errors.push('Plan locale is invalid.');
  if (!isLocalizedText(plan.title)) errors.push('Plan title must be localized.');
  if (!isLocalizedText(plan.summary)) errors.push('Plan summary must be localized.');
  if (!Array.isArray(plan.sections)) errors.push('Plan sections must be an array.');
  if (!Array.isArray(plan.warnings)) errors.push('Plan warnings must be an array.');
  if (!Array.isArray(plan.actions)) errors.push('Plan actions must be an array.');

  (plan.actions || []).forEach((planAction, actionIndex) => {
    if (!isLocalizedText(planAction.label)) errors.push(`Action ${actionIndex} label must be localized.`);
  });

  (plan.warnings || []).forEach((planWarning, warningIndex) => {
    if (!isLocalizedText(planWarning.message)) errors.push(`Warning ${warningIndex} message must be localized.`);
  });

  (plan.sections || []).forEach((section, sectionIndex) => {
    if (!isLocalizedText(section.title)) errors.push(`Section ${sectionIndex} title must be localized.`);
    if (!Array.isArray(section.items)) errors.push(`Section ${sectionIndex} items must be an array.`);
    (section.items || []).forEach((item, itemIndex) => {
      if (item.source !== 'catalog') errors.push(`Item ${sectionIndex}.${itemIndex} must come from the directory.`);
      if (!isLocalizedText(item.title)) errors.push(`Item ${sectionIndex}.${itemIndex} title must be localized.`);
      if (!isLocalizedText(item.reason)) errors.push(`Item ${sectionIndex}.${itemIndex} reason must be localized.`);
      if (!Array.isArray(item.actions)) errors.push(`Item ${sectionIndex}.${itemIndex} actions must be an array.`);
      (item.actions || []).forEach((itemAction, actionIndex) => {
        if (!isLocalizedText(itemAction.label)) errors.push(`Item ${sectionIndex}.${itemIndex} action ${actionIndex} label must be localized.`);
      });
      if (item.source === 'catalog') {
        const ref = item.ref;
        const collection = ref && REF_COLLECTIONS[ref.type];
        if (!collection || !references[collection]?.has(String(ref.id))) {
          errors.push(`Item ${sectionIndex}.${itemIndex} references an unknown directory item.`);
        }
      }
    });
  });

  return { ok: errors.length === 0, errors };
}

const catalogLookup = catalog => ({
  places: new Map((catalog.places || []).map(place => [String(place.id), place])),
  businesses: new Map((catalog.businesses || []).map(business => [String(business.id), business])),
  events: new Map((catalog.events || []).map(event => [String(event.id), event])),
});

const itemForRef = (lookup, ref) => {
  if (!ref) return null;
  if (ref.type === 'place') return lookup.places.get(String(ref.id)) || null;
  if (ref.type === 'business') return lookup.businesses.get(String(ref.id)) || null;
  if (ref.type === 'event') return lookup.events.get(String(ref.id)) || null;
  return null;
};

const googleMapsUrl = (item, title) => {
  if (hasCoordinates(item)) {
    return `https://www.google.com/maps/search/?api=1&query=${item.coordinates.lat},${item.coordinates.lng}`;
  }
  const caza = normalizeCatalogCaza(item?.caza);
  const region = caza === 'bsharri' ? 'Bsharri Lebanon' : caza === 'zgharta' ? 'Zgharta Lebanon' : 'North Lebanon';
  const query = [title?.en, item?.village, region].filter(Boolean).join(' ');
  return query ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}` : null;
};

export function buildSavedAskZghartaStopSnapshots(plan, catalog = {}) {
  const lookup = catalogLookup(catalog);
  return (plan.sections || []).flatMap(section => (section.items || []).map(item => {
    const catalogItemRef = itemForRef(lookup, item.ref);
    return {
      itemId: item.id,
      ref: item.ref || null,
      title: item.title,
      village: catalogItemRef?.village || null,
      caza: normalizeCatalogCaza(catalogItemRef?.caza) || null,
      mapUrl: googleMapsUrl(catalogItemRef, item.title),
    };
  }));
}

export function createSavedAskZghartaPlanRecord(plan, catalog = {}, timestamp = new Date().toISOString()) {
  return {
    version: SAVED_ASK_ZGHARTA_PLANS_VERSION,
    id: plan.id,
    plan,
    savedAt: timestamp,
    updatedAt: timestamp,
    stops: buildSavedAskZghartaStopSnapshots(plan, catalog),
  };
}

const isLegacyOrCurrentSavedPlanRecord = value => (
  value &&
  typeof value === 'object' &&
  (value.version === SAVED_ASK_ZGHARTA_PLANS_VERSION || value.version === LEGACY_SAVED_ASK_ZGHARTA_PLANS_VERSION) &&
  typeof value.id === 'string' &&
  value.plan &&
  Array.isArray(value.stops)
);

function migrateSavedPlanRecord(record) {
  if (!isLegacyOrCurrentSavedPlanRecord(record)) return null;
  return {
    ...record,
    version: SAVED_ASK_ZGHARTA_PLANS_VERSION,
    stops: record.stops.map(stop => ({
      ...stop,
      caza: normalizeCatalogCaza(stop?.caza) || null,
    })),
  };
}

export function parseSavedAskZghartaPlans(raw) {
  if (!raw) return EMPTY_SAVED_ASK_ZGHARTA_PLANS;
  try {
    const parsed = JSON.parse(raw);
    if (
      !parsed ||
      (parsed.version !== SAVED_ASK_ZGHARTA_PLANS_VERSION && parsed.version !== LEGACY_SAVED_ASK_ZGHARTA_PLANS_VERSION) ||
      !Array.isArray(parsed.plans)
    ) {
      return EMPTY_SAVED_ASK_ZGHARTA_PLANS;
    }
    return {
      version: SAVED_ASK_ZGHARTA_PLANS_VERSION,
      plans: parsed.plans.map(migrateSavedPlanRecord).filter(Boolean),
    };
  } catch {
    return EMPTY_SAVED_ASK_ZGHARTA_PLANS;
  }
}

export function saveAskZghartaPlanState(state, plan, catalog = {}, timestamp = new Date().toISOString()) {
  const current = state?.version === SAVED_ASK_ZGHARTA_PLANS_VERSION ? state : EMPTY_SAVED_ASK_ZGHARTA_PLANS;
  const existing = current.plans.find(record => record.id === plan.id);
  const nextRecord = {
    ...createSavedAskZghartaPlanRecord(plan, catalog, timestamp),
    savedAt: existing?.savedAt || timestamp,
  };

  return {
    version: SAVED_ASK_ZGHARTA_PLANS_VERSION,
    plans: [nextRecord, ...current.plans.filter(record => record.id !== plan.id)],
  };
}

export function removeAskZghartaPlanState(state, planId) {
  const current = state?.version === SAVED_ASK_ZGHARTA_PLANS_VERSION ? state : EMPTY_SAVED_ASK_ZGHARTA_PLANS;
  return {
    version: SAVED_ASK_ZGHARTA_PLANS_VERSION,
    plans: current.plans.filter(record => record.id !== planId),
  };
}

export const isAskZghartaPlanSaved = (state, planId) => (
  Boolean(planId) && (state?.plans || []).some(record => record.id === planId)
);

export function buildSavedAskZghartaPlanShareMessage(record, locale = 'en') {
  const matchesLocale = value => {
    const text = String(value || '').trim();
    if (!text) return false;
    return locale === 'ar' ? !/[A-Za-z]/.test(text) : !/[\u0600-\u06FF]/.test(text);
  };
  const ownership = askZghartaOwnershipCopy(record?.stops || [], locale);
  const storedTitle = localizedAskZghartaText(record.plan.title, locale);
  const lines = [
    ownership.title,
    storedTitle && storedTitle !== ownership.title ? storedTitle : localizedAskZghartaText(record.plan.summary, locale),
    '',
  ];

  record.stops.forEach((stop, index) => {
    lines.push(`${index + 1}. ${localizedAskZghartaText(stop.title, locale)}`);
    if (matchesLocale(stop.village)) lines.push(stop.village);
    if (stop.mapUrl) lines.push(stop.mapUrl);
  });

  return lines.filter((line, index, all) => line || all[index - 1]).join('\n').trim();
}
